import i18n from '@link/share/i18n';
import { buildPathWithRedirect, resolveLoginReturnPath } from './redirect';

const ABSOLUTE_URL_PATTERN = /^[a-zA-Z][a-zA-Z\d+\-.]*:\/\//;

const hasProtocol = (path: string): boolean => {
  return ABSOLUTE_URL_PATTERN.test(path) || path.startsWith('//');
};

const isAlreadyLocalized = (path: string, lang: string): boolean => {
  return path === lang || path.startsWith(`${lang}/`);
};

const normalizePath = (path: string): string => {
  return path.replace(/^\/+/, '');
};

/**
 * Prefix a relative path with the current locale segment (e.g. /ja/terms).
 * Absolute URLs and protocol-relative URLs are returned as-is.
 *
 * `lang` defaults to the i18next singleton's language. A server render must
 * pass it: the singleton is shared by every request the process is handling,
 * so reading it there could build a link in another visitor's language.
 */
export const buildLocalizedPath = (
  path: string = '',
  lang: string = i18n.language
): string => {
  const trimmedPath = path.trim();

  if (!trimmedPath || trimmedPath === '/') {
    return `/${lang}`;
  }

  if (hasProtocol(trimmedPath)) {
    return trimmedPath;
  }

  if (trimmedPath.startsWith('?') || trimmedPath.startsWith('#')) {
    return `/${lang}${trimmedPath}`;
  }

  const normalized = normalizePath(trimmedPath);

  if (!normalized) {
    return `/${lang}`;
  }

  if (isAlreadyLocalized(normalized, lang)) {
    return `/${normalized}`;
  }

  return `/${lang}/${normalized}`;
};

/** Localized login URL that returns to `returnPath` after sign-in. */
export const buildLoginPathWithRedirect = (returnPath: string): string =>
  buildPathWithRedirect(buildLocalizedPath('login'), returnPath);

/** Login URL for a session that ended on the current page: returns here. */
export const buildLoginPathForCurrentPage = (): string => {
  const { pathname, search } = window.location;
  const returnPath = resolveLoginReturnPath(pathname, search);
  return returnPath
    ? buildLoginPathWithRedirect(returnPath)
    : buildLocalizedPath('login');
};
